import { FormattedMessage } from 'react-intl';
import { RequestClass, SharingDataRequest } from 'acscp/api/requests';
import { Dispatch } from 'redux';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState, Booking, SharingType } from 'acscp/store';
import React from 'react';
import { LoadingIndicator } from '../common/loadingIndicator';
import { FetchRequest } from 'acscp/actions';
import { SharingForm } from 'acscp/components/sharing/sharingForm';
import { RequestStateProps, } from 'acscp/components/common/helpers/requestStateProps';
import style from './sharing.module.scss';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { UnshareResharePopup } from './unshareResharePopup';
import classNames from 'classnames';

interface SharingPageOwnProps {
    sharingTypes: SharingType[];
    booking: Booking;
}

interface SharingPageStateProps {
    alreadySharedEmails: string[];
    isLoading: boolean;
}

interface SharingPageDispatchProps {
    fetchSharingData: () => void;
}

interface SharingPageInternalState {
    showSharingPopup: boolean;
    sharingPopupEmail: string;
    sharingPopupSource: HTMLDivElement | null;
    highlightEmailIndex: number;
}

export class SharingPagePresentational extends PresentationComponentBase<SharingPageOwnProps,
    SharingPageStateProps, SharingPageDispatchProps, SharingPageInternalState> {

    constructor(props: SharingPageOwnProps & SharingPageStateProps & SharingPageDispatchProps) {
        super(props);
        this.state = {
            showSharingPopup: false,
            sharingPopupEmail: '',
            sharingPopupSource: null,
            highlightEmailIndex: -1
        };
        this.closeSharingPopup = this.closeSharingPopup.bind(this);
        this.emailElements = Array<HTMLDivElement | null>();
    }

    private emailElements: Array<HTMLDivElement | null>;

    componentDidMount(): void {
        this.props.fetchSharingData();
    }

    launchSharingPopup(email: string, ref: HTMLDivElement | null, index: number): void {
        this.setState({
            showSharingPopup: true,
            sharingPopupEmail: email,
            sharingPopupSource: ref,
            highlightEmailIndex: index
        });
    }

    closeSharingPopup(): void {
        this.setState({
            showSharingPopup: false,
            highlightEmailIndex: -1
        });
    }

    render(): JSX.Element {
        if (this.props.isLoading) {
            return (<LoadingIndicator />);
        }

        return (
            <div className={style.SharingPage}>
                <div className={style.Title}>
                    <FormattedMessage
                        id={'components.sharingPage.addEmailsToShareWith'}
                        defaultMessage={'ADD EMAILS TO SHARE WITH'}
                    />
                </div>
                <SharingForm
                    sharingTypes={this.props.sharingTypes}
                    bookingId={this.props.booking.id}
                />
                <div className={style.CurrentlySharingTitle}>
                    <FormattedMessage
                        id={'components.sharingPage.currentlySharingWith'}
                        defaultMessage={'YOU HAVE PREVIOUSLY SHARED THIS WITH:'}
                    />
                </div>
                <div className={style.SharedEmails}>
                    <div className={style.InnerSharedContainer}>
                        {this.props.alreadySharedEmails.map((email, index) => (
                            <div
                                className={classNames(style.SharedLine, {[style.SelectedEmail]: index === this.state.highlightEmailIndex})}
                                key={index}
                            >
                                <div className={style.ShareEmail}>{email}</div>
                                <div
                                    className={style.ShareControl}
                                    ref={node => this.emailElements[index] = node}
                                    onClick={() => this.launchSharingPopup(email, this.emailElements[index], index)}
                                >
                                    <span className={style.Dot} />
                                    <span className={style.Dot} />
                                    <span className={style.Dot} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <UnshareResharePopup
                    showPopup={this.state.showSharingPopup}
                    bookingId={this.props.booking.id}
                    email={this.state.sharingPopupEmail}
                    sharingTypes={this.props.sharingTypes}
                    closer={this.closeSharingPopup}
                    sourceRef={this.state.sharingPopupSource}
                />
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: SharingPageOwnProps, getRequestError: (request: RequestClass<any>) => RequestStateProps
): SharingPageStateProps {
    const sharingData = state.sharingData[ownProps.booking.id] || [];
    const alreadySharedEmails = sharingData
        .filter(sharingObject => ownProps.sharingTypes.every(type => sharingObject.types.indexOf(type) > -1))
        .map(object => object.email.toLowerCase())
        .sort();

    const requestStateProps = getRequestError(SharingDataRequest);

    return {
        alreadySharedEmails,
        isLoading: requestStateProps.isLoading
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: SharingPageOwnProps): SharingPageDispatchProps {
    return {
        fetchSharingData: () => dispatch(new FetchRequest(new SharingDataRequest(ownProps.booking.id)))
    };
}

export const SharingPage = connectWithRequests(mapStateToProps, mapDispatchToProps)(SharingPagePresentational);
